import { Button, Flex, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { MapPinArea, Info } from "@phosphor-icons/react";
import { P } from "../../atoms/paragraph/Paragraph.styled";
import { useAppContext } from "../../../contexts/useAppContext";
import { legacyPathToPage } from "../../../utils/navigation";
import { MenuPage } from "../../../types/cart.types";
import { useQuery } from "react-query";
import { getProductCategories, getServices } from "../../../service";
import styled from "styled-components";
import { ReactComponent as CaretLeftIcon } from "../../../images/CaretLeft.svg";
import { ThemeToggle } from "../../atoms/ThemeToggle";
import { LanguageSwitcher } from "../../atoms/LanguageSwitcher";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ADMIN_URL = process.env.REACT_APP_ADMIN_URL || 'https://admin.proste.menu';

// ─── Legacy header styles (non-dishes pages) ────────────────────────────────

const headerStyle: React.CSSProperties = {
  height: "100%",
  paddingLeft: "8px",
  paddingRight: "8px",
  maxWidth: "800px",
  width: "100%",
};

const CostumButton = styled(Button)`
  max-height: 35px !important;
  height: 35px !important;
  border-radius: 30px;
  color: var(--color-primary);
  &:hover {
    color: var(--color-primary-hover) !important;
    border-color: var(--color-primary-hover) !important;
  }
`;

// ─── New dishes-page header styles ──────────────────────────────────────────

const DishesOuterWrapper = styled.div`
  display: flex;
  justify-content: center;
  background: var(--color-background-secondary);

  @media (min-width: 800px) {
    background: var(--color-surface);
  }
`;

const DishesHeaderWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  background: var(--color-background-secondary);
  padding-top: var(--spacing-3);

  @media (min-width: 800px) {
    background: var(--color-surface);
  }
`;

const BannerWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #e0e0e0;
  border-radius: 16px;

  @media (min-width: 800px) {
    aspect-ratio: 16 / 9;
  }
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const BannerPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #d0d0d0 0%, #b0b0b0 100%);
`;

const ReservationButton = styled(Button)`
  position: absolute;
  top: 12px;
  right: 12px;
  border-radius: 30px;
  color: #1677ff;
  background: #ffffff;
  border-color: #ffffff;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  &:hover {
    background: #f0f0f0 !important;
    border-color: #f0f0f0 !important;
    color: #1677ff !important;
  }
`;

const ThemeToggleWrapper = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
`;

const LOGO_SIZE = "clamp(4rem, 10vw, 5rem)";

const LogoThumb = styled.div`
  width: ${LOGO_SIZE};
  height: ${LOGO_SIZE};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ContentContainer = styled.div`
  padding: var(--spacing-6) var(--spacing-4);
  background: var(--color-surface);
`;

const BranchInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

const BranchInfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-2);
`;

const BranchButtons = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  margin-left: auto;
`;

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HeaderAdditionalButton = styled(Button)`
  border-radius: 30px;
  height: 32px;
  padding: 0 var(--spacing-3);
  font-size: var(--font-size-body-small);
  flex-shrink: 0;
  color: var(--color-primary);
  &:hover {
    color: var(--color-primary-hover) !important;
    border-color: var(--color-primary-hover) !important;
  }
`;

const InfoButton = styled(Button)`
  border-radius: 30px;
  width: 32px;
  height: 32px;
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  border: none;
  box-shadow: none;
  &:hover {
    color: var(--color-primary-hover) !important;
    background: var(--color-primary-bg) !important;
  }
`;

const ClaimButton = styled(Button)`
  border-radius: 30px;
  height: 32px;
  padding: 0 var(--spacing-3);
  font-size: var(--font-size-body-small);
  flex-shrink: 0;
  background: var(--color-primary) !important;
  border-color: var(--color-primary) !important;
  color: var(--color-text-inverse) !important;
  &:hover {
    background: var(--color-primary-hover) !important;
    border-color: var(--color-primary-hover) !important;
    color: var(--color-text-inverse) !important;
  }
  &:active {
    background: var(--color-primary-active) !important;
    border-color: var(--color-primary-active) !important;
    color: var(--color-text-inverse) !important;
  }
`;



// ─── Helpers ────────────────────────────────────────────────────────────────

const formatAddressShort = (address: string): string => {
  const parts = address.split(',').map(p => p.trim());
  // Usuń ostatnią część jeśli nie zawiera cyfr (kraj)
  const withoutCountry = /\d/.test(parts[parts.length - 1]) ? parts : parts.slice(0, -1);
  // Usuń kod pocztowy (XX-XXX ) z każdej części
  return withoutCountry.map(p => p.replace(/\d{2}-\d{3}\s+/g, '').trim()).join(', ');
};

type TimeRange = { from: string; till: string };
type Schedule = { [key: string]: TimeRange[] };

const formatScheduleForModal = (
  schedule: Schedule,
  t: (key: string) => string
) => {
  const dayNames: { [key: string]: string } = {
    monday: t("headerComponent.dayMonday"),
    tuesday: t("headerComponent.dayTuesday"),
    wednesday: t("headerComponent.dayWednesday"),
    thursday: t("headerComponent.dayThursday"),
    friday: t("headerComponent.dayFriday"),
    saturday: t("headerComponent.daySaturday"),
    sunday: t("headerComponent.daySunday"),
  };

  return Object.entries(schedule)
    .sort((a, b) => Object.keys(dayNames).indexOf(a[0]) - Object.keys(dayNames).indexOf(b[0]))
    .map(([day, timeRanges]) => {
      if (timeRanges.length === 0) {
        return (
          <Flex key={day} justify="space-between" style={{ marginBottom: "8px" }}>
            <P $size="var(--font-size-body-small)" $weight="var(--font-weight-normal)" $height="var(--line-height-body)">{dayNames[day]}</P>
            <P $size="var(--font-size-body-small)" $weight="var(--font-weight-normal)" $height="var(--line-height-body)">{t("headerComponent.closed")}</P>
          </Flex>
        );
      }
      return (
        <Flex key={day} vertical style={{ marginBottom: "8px" }}>
          {timeRanges.map((range, index) => (
            <Flex key={`${day}-${index}`} justify="space-between">
              <P $size="var(--font-size-body-small)" $weight="var(--font-weight-normal)" $height="var(--line-height-body)">
                {index === 0 ? dayNames[day] : ""}
              </P>
              <P $size="var(--font-size-body-small)" $weight="var(--font-weight-normal)" $height="var(--line-height-body)">
                {`${range.from} - ${range.till}`}
              </P>
            </Flex>
          ))}
        </Flex>
      );
    });
};

// ─── Component ───────────────────────────────────────────────────────────────

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    selectedBranch,
    isMain,
    setIsMain,
    setCart,
    setDeliveryType,
    setDeliveryTimeType,
    setOrderDate,
    setOrderTime,
    setServiceData,
    setServiceTime,
    setServiceResurces,
    setSelectedDeliveryMethod,
    cart,
    currentPage,
    navigateTo,
    goBack,
  } = useAppContext();

  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const claimToken = new URLSearchParams(location.search).get('claim');

  useEffect(() => {
    // Remove all existing icon links to avoid conflicts
    document.querySelectorAll("link[rel~='icon']").forEach((el) => el.remove());

    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/png";
    link.href = selectedBranch?.logoUrl || "https://framerusercontent.com/images/7b83L9rGtyzIPANu7gWnmvve10.png";
    document.head.appendChild(link);
  }, [selectedBranch?.logoUrl]);

  useEffect(() => {
    if (claimToken) {
      localStorage.setItem('pendingClaimToken', claimToken);
    }
  }, [claimToken]);

  const handleRegisterWithClaim = () => {
    const registerUrl = `${ADMIN_URL}/register${claimToken ? `?claim=${claimToken}` : ''}`;
    window.location.href = registerUrl;
  };

  const page = currentPage.page;

  const additionalButton = selectedBranch?.config?.welcome_screen?.additional_button;
  const mainButton = selectedBranch?.config?.welcome_screen?.main_button;

  const { data: productCategories = [] } = useQuery(
    ["/product-categories", selectedBranch?.id],
    () => getProductCategories({ id: selectedBranch?.id || 0 }),
    { enabled: !!selectedBranch }
  );

  const { data: serviceCategories = [] } = useQuery(
    ["/services", selectedBranch?.id],
    () => getServices(Number(selectedBranch?.id) || 0),
    { enabled: !!selectedBranch }
  );

  const hasProducts = productCategories.length > 0;
  const hasServices = serviceCategories.length > 0;

  const targetNavigate = isMain ? additionalButton?.navigate : mainButton?.navigate;
  const isTargetVisible = targetNavigate === 'services' ? hasServices : (targetNavigate === 'dishes' ? hasProducts : true);

  const redirect = () => {
    setCart([]);
    setDeliveryType("TO_THE_ADDRESS");
    setDeliveryTimeType("NOW");
    setOrderDate(null);
    setOrderTime(null);
    setServiceData(null);
    setServiceTime(null);
    setServiceResurces({});
    setSelectedDeliveryMethod(null);
    setIsMain(!isMain);
    navigateTo(
      legacyPathToPage(isMain ? additionalButton?.navigate : mainButton?.navigate) as MenuPage
    );
  };

  const handleNavButtonClick = () => {
    if (cart?.length === 0) {
      redirect();
    } else {
      Modal.info({
        title: t("headerComponent.cartWillBeEmptiedTitle"),
        content: (
          <div>
            <p>{t("headerComponent.confirmNavigateContent")}</p>
          </div>
        ),
        closable: true,
        onOk() {
          redirect();
        },
        okText: t("headerComponent.confirmButton"),
      });
    }
  };

  if (page === "statute") return null;
  if (!selectedBranch) return null;

  // ─── New header for dishes/services page ─────────────────────────────────
  if (page === "dishes" || page === "services") {
    const isReservationActive =
      !!additionalButton && !!mainButton && isTargetVisible && !claimToken;

    return (
      <DishesOuterWrapper>
        <DishesHeaderWrapper>
          {/* [1] Banner */}
          {selectedBranch?.bannerImage && (
            <BannerWrapper>
              <BannerImg src={selectedBranch.bannerImage} alt="banner" />
            </BannerWrapper>
          )}

          {/* [3] Content */}
          <ContentContainer>
            <BranchInfoColumn>
              {/* Logo + buttony */}
              <BranchInfoRow>
                {selectedBranch?.logoUrl && (
                  <LogoThumb>
                    <img src={selectedBranch.logoUrl} alt={selectedBranch.name} />
                  </LogoThumb>
                )}
                <BranchButtons>
                  {isReservationActive && (
                    <HeaderAdditionalButton onClick={handleNavButtonClick}>
                      {isMain ? additionalButton?.text : mainButton?.text}
                    </HeaderAdditionalButton>
                  )}
                  {claimToken && (
                    <ClaimButton onClick={handleRegisterWithClaim}>
                      {t("headerComponent.createFreeAccount")}
                    </ClaimButton>
                  )}
                  <InfoButton onClick={() => setInfoModalOpen(true)}>
                    <Info size={18} />
                  </InfoButton>
                  <LanguageSwitcher size="small" bordered={false} />
                </BranchButtons>
              </BranchInfoRow>

              {/* Nazwa */}
              <P
                $size="var(--font-size-heading2)"
                $height="var(--line-height-heading-tight)"
                $weight="700"
              >
                {selectedBranch?.name}
              </P>

              {/* Adres */}
              {selectedBranch?.formattedAddress && (
                <AddressRow>
                  <MapPinArea size={14} color="var(--color-text-secondary)" weight="bold" style={{ flexShrink: 0 }} />
                  <P
                    $size="var(--font-size-body-small)"
                    $height="var(--line-height-body)"
                    $weight="var(--font-weight-normal)"
                    style={{ color: "var(--color-text-secondary)" }}
                  >
                    {formatAddressShort(selectedBranch.formattedAddress)}
                  </P>
                </AddressRow>
              )}

              {/* Opis */}
              {selectedBranch?.description && (
                <P
                  $size="var(--font-size-body-small)"
                  $height="var(--line-height-body)"
                  $weight="var(--font-weight-normal)"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  {selectedBranch.description}
                </P>
              )}
            </BranchInfoColumn>
          </ContentContainer>
        </DishesHeaderWrapper>

        {/* [8] Info modal */}
        <Modal
          open={infoModalOpen}
          onCancel={() => setInfoModalOpen(false)}
          footer={null}
          title={
            <P $size="var(--font-size-heading2)" $height="var(--line-height-heading-tight)" $weight="700">
              {t("headerComponent.branchInfoTitle")}
            </P>
          }
          closable
        >
          <Flex vertical gap={0}>
            {/* Godziny przyjmowania zamówień */}
            {Object.keys(selectedBranch?.openingHours || {}).length > 0 && (
              <Flex vertical gap={8} style={{ padding: "16px 0" }}>
                <P $size="var(--font-size-body-large)" $weight="600" $height="var(--line-height-body)">
                  {t("headerComponent.orderHours")}
                </P>
                <Flex vertical gap={0}>
                  {formatScheduleForModal(selectedBranch?.openingHours, t)}
                </Flex>
              </Flex>
            )}

            {/* Adres lokalu */}
            {selectedBranch?.formattedAddress && (
              <Flex vertical gap={4} style={{ padding: "16px 0" }}>
                <P $size="var(--font-size-body-large)" $weight="600" $height="var(--line-height-body)">
                  {t("headerComponent.address")}
                </P>
                <P $size="var(--font-size-body-small)" $weight="var(--font-weight-normal)" $height="var(--line-height-body)">
                  {selectedBranch.formattedAddress}
                </P>
              </Flex>
            )}

            {/* Dane kontaktowe */}
            {(selectedBranch?.phone || selectedBranch?.email) && (
              <Flex vertical gap={4} style={{ padding: "16px 0" }}>
                <P $size="var(--font-size-body-large)" $weight="600" $height="var(--line-height-body)">
                  {t("headerComponent.contact")}
                </P>
                {selectedBranch?.phone && (
                  <P $size="var(--font-size-body-small)" $weight="var(--font-weight-normal)" $height="var(--line-height-body)">
                    {t("headerComponent.phoneLabel")} <a href={`tel:${selectedBranch.phone}`} style={{ color: "var(--color-primary)" }}>{selectedBranch.phone}</a>
                  </P>
                )}
                {selectedBranch?.email && (
                  <P $size="var(--font-size-body-small)" $weight="var(--font-weight-normal)" $height="var(--line-height-body)">
                    {t("headerComponent.emailLabel")} <a href={`mailto:${selectedBranch.email}`} style={{ color: "var(--color-primary)" }}>{selectedBranch.email}</a>
                  </P>
                )}
              </Flex>
            )}

            {/* Stopka */}
            <P
              $size="var(--font-size-body-small)"
              $weight="var(--font-weight-normal)"
              $height="var(--line-height-body)"
              style={{ color: "var(--color-text-secondary)", textAlign: "center", padding: "16px 0", borderTop: "1px solid var(--color-border)" }}
            >
              {t("headerComponent.poweredBy")}{" "}
              <a href="https://app.proste.menu" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-text-primary)", fontWeight: 600, textDecoration: "underline" }}>proste.menu</a>
            </P>
          </Flex>
        </Modal>
      </DishesOuterWrapper>
    );
  }

  // ─── Legacy header for all other pages ────────────────────────────────────
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        background: "var(--color-background-secondary)",
        borderBottom: "2px solid var(--color-border)",
        paddingTop: "20px",
        paddingBottom: "20px",
      }}
    >
      <Flex style={headerStyle} align="center" justify="space-between" gap={8}>
        <Flex align="center" justify="space-between" gap={8}>
          <div>
            {page !== "thank-you" && (
              <Button type="text" onClick={() => goBack()}>
                <Flex align="center" gap={12}>
                  <CaretLeftIcon style={{ color: "var(--color-primary)" }} />
                  {page === "cart" ? <span className="cart">{t("common.cart")}</span> : null}
                  {page === "delivery" ? <span className="delivery">{t("common.deliveryData")}</span> : null}
                  {page === "summary" ? <span className="delivery">{t("common.summary")}</span> : null}
                </Flex>
              </Button>
            )}
          </div>
        </Flex>
        <Flex align="center" justify="space-between" gap={8}>
          {claimToken && (
            <ClaimButton onClick={handleRegisterWithClaim}>
              {t("common.createFreeAccount")}
            </ClaimButton>
          )}
          <LanguageSwitcher size="small" bordered={false} />
          {window.location.hostname === 'localhost' && <ThemeToggle />}
        </Flex>
      </Flex>
    </div>
  );
};
